import { useCallback, useContext } from 'react';
import { downloadCaseNotesPDF } from 'services/CaseNoteServices';
import { useNavbarContext } from 'context/NavbarContext';
import { uuidv4 } from 'common/uuidGenerator';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { UserDetailsContext } from 'context/userDetailsContext';
import { getTimezoneOffset } from 'utils/utility_functions/timezone';

export default function useCaseNoteDownload() {
  const userContext = useContext(UserDetailsContext);
  const { addFolderDownload } = useNavbarContext();
  const { addToast } = useToast();

  const downloadCaseNotesAsPDF = useCallback(
    async (caseId, filters = {}) => {
      try {
        const downloadId = uuidv4();
        const downloadType = filters.download_type || 'case_note';
        let folderName, type;

        if (downloadType === 'call_log') {
          folderName = `Call Logs`;
          type = 'call_logs';
        } else if (downloadType === 'secure_message') {
          folderName = `Secure Messages`;
          type = 'secure_message';
        } else if (downloadType === 'case_note&call_log') {
          folderName = `Case Notes and Call Logs`;
          type = 'case_notes_call_logs';
        } else {
          folderName = `Case Notes`;
          type = 'case_notes';
        }

        const downloadData = {
          id: downloadId,
          folderName: folderName,
          caseId: caseId,
          status: 'In Progress',
          progress: 0,
          zipFile: null,
          error: null,
          type: type,
        };
        // Add to folder downloads context
        addFolderDownload(downloadData);
        const requestData = {
          case_id: caseId,
          case_name: filters.case_name || '',
          download_type: downloadType,
          message_thread_pk: filters.message_thread_pk || null,
          timezone_offset: getTimezoneOffset(userContext?.userDetails?.timezone),
        };

        // Call the PDF download API
        await downloadCaseNotesPDF(requestData);

        // The API call will trigger a WebSocket message when the PDF is ready
        // The WebSocket handler in NavbarContext will handle the completion
      } catch (error) {
        console.error('Error downloading PDF:', error);
        const downloadType = filters.download_type || 'case_note';
        let folderName, type, errorMessage;

        if (downloadType === 'call_log') {
          folderName = `Call Logs`;
          type = 'call_logs_pdf';
          errorMessage = 'Failed to initiate call logs PDF download.';
        } else if (downloadType === 'secure_message') {
          folderName = `Secure Messages`;
          type = 'secure_message_pdf';
          errorMessage = 'Failed to initiate secure message PDF download.';
        } else if (downloadType === 'case_note&call_log') {
          folderName = `Case Notes and Call Logs`;
          type = 'case_notes_call_logs';
        } else {
          folderName = `Case Notes`;
          type = 'case_notes_pdf';
          errorMessage = 'Failed to initiate case notes PDF download.';
        }

        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, errorMessage);
        addFolderDownload({
          id: uuidv4(),
          folderName: folderName,
          caseId: caseId,
          status: 'failed',
          error: error.message || errorMessage,
          type: type,
        });
      }
    },
    [addFolderDownload, addToast]
  );

  return { downloadCaseNotesAsPDF };
}
